import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import { useMemo, useState, Fragment, useCallback } from 'react';

import { Box, Stack, useTheme } from '@mui/system';
import {
  Card,
  List,
  Button,
  Divider,
  ListItem,
  TextField,
  Pagination,
  CardHeader,
  CardContent,
  Autocomplete,
  ListItemText,
  CircularProgress
} from '@mui/material';

import { paths } from 'src/routes/paths';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { PageOptions } from 'src/services/api.types';
import { EntityLinkType } from 'src/services/common.types';
import { IDealCompanyLink } from 'src/services/deal-flow/deal-flow.types';
import { ICompanyProfile } from 'src/services/connections/connections.types';
import { useGetMyCompaniesQuery } from 'src/services/connections/connections.service';
import {
  useGetDealCompanyLinksQuery,
  useAddDealCompanyLinksMutation,
  useRemoveDealCompanyLinkMutation
} from 'src/services/deal-flow/deal-flow.service';

import Iconify from 'src/components/iconify';
import EmptyContent from 'src/components/empty-content';
import { ConfirmDialog } from 'src/components/custom-dialog';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

import { LoadingCardItem } from 'src/sections/admin/billing/payment/payment-card-item';

import { TenantType } from 'src/types/enums';

// ----------------------------------------------------------------------

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 5,
};

type Props = {
  dealId: string;
  /** Open a linked company in place (an overlay stacked on the deal) instead of navigating away (JT1-1736). */
  onOpenCompany?: (companyProfileId: string) => void;
};

export function DealLinkedCompaniesSection({ dealId, onOpenCompany }: Props) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const tenantType = useOrgTenant();

  const [pageOptions, setPageOptions] = useState(DEFAULT_PAGE);
  const [searchQuery, setSearchQuery] = useState('');
  const [companySearchQuery, setCompanySearchQuery] = useState('');

  const addLinkControl = useBoolean();

  const { data: companyLinksData, isLoading: isLoadingCompanyLinks } = useGetDealCompanyLinksQuery({
    dealId,
    page: pageOptions.page,
    perPage: pageOptions.per_page,
    searchQuery,
  });

  const { currentData: companies, isLoading: isConnectionsLoading } = useGetMyCompaniesQuery(
    {
      params: {
        ...pageOptions,
        search_query: companySearchQuery
      },
    }
  );

  const [addCompanyLinks, { isLoading: isAddingCompanyLinks }] = useAddDealCompanyLinksMutation();
  const [removeCompanyLink, { isLoading: isRemovingCompanyLink }] = useRemoveDealCompanyLinkMutation();

  const [isSearchingCompanies, setIsSearchingCompanies] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<ICompanyProfile | null>(null);
  const [selectedLinkType, setSelectedLinkType] = useState<EntityLinkType>(EntityLinkType.OTHER);

  const handleSearchChange = useMemo(
    () => debounce((value: string) => {
      setSearchQuery(value);
    }, 500),
    [setSearchQuery]
  );

  const onLinkModalClose = useCallback(() => {
    addLinkControl.onFalse();
    setSelectedCompany(null);
    setSelectedLinkType(EntityLinkType.OTHER);
    setCompanySearchQuery('');
  }, [addLinkControl]);

  const handleAddCompanies = async () => {
    if (!selectedCompany) return;

    try {
      await addCompanyLinks({
        dealId,
        data: {
          company_profiles: [{
            company_profile_id: selectedCompany.id,
            type: selectedLinkType,
          }],
        },
      }).unwrap();

      enqueueSnackbar(t('deal_flow.api.add_company_links.success'), { variant: 'success' });
      onLinkModalClose();
    } catch (error) {
      enqueueSnackbar(t('deal_flow.api.add_company_links.default_error'), { variant: 'error' });
    }
  };

  const handleRemoveCompany = useCallback(async (linkId: string) => {
    try {
      await removeCompanyLink({
        dealId,
        linkId,
      }).unwrap();

      enqueueSnackbar(t('deal_flow.api.remove_company_link.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('deal_flow.api.remove_company_link.default_error'), { variant: 'error' });
    }
  }, [dealId, removeCompanyLink, enqueueSnackbar, t]);

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setCompanySearchQuery(value);
    }, 500),
    [setCompanySearchQuery]
  );

  const onChangePage = useCallback((event: React.ChangeEvent<unknown>, pageNumber: number) => {
    setPageOptions((prev) => ({
      ...prev,
      page: pageNumber
    }));
  }, []);

  const onAddLink = () => {
    addLinkControl.onTrue();
  };

  const linkableCompanies = useMemo(() => {
    if (isConnectionsLoading || isLoadingCompanyLinks) return [];

    return companies?.results?.filter((company) => {
      const isAlreadyLinked = companyLinksData?.results?.some((link) => link.company_profile.id === company.id);
      return !isAlreadyLinked;
    });
  }, [companies, companyLinksData, isConnectionsLoading, isLoadingCompanyLinks]);

  return (
    <>
      <Card>
        <CardHeader
          title={
            <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
              <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
                <Iconify
                  icon="material-symbols:dataset-linked-outline" />
                <Box>{t('deal_flow.detail.linked_companies.title')}</Box>
              </Stack>
              <Button
                variant="outlined"
                size="small"
                onClick={onAddLink}
                startIcon={<Iconify icon="eva:plus-fill" />}
                sx={{
                  color: 'text.primary',
                  textTransform: 'capitalize',
                }}>
                {t('deal_flow.detail.linked_companies.add')}
              </Button>
            </Stack>
          }
        />
        <CardContent sx={{ mb: 2 }}>
          {isLoadingCompanyLinks && (
            <Stack sx={{ width: '100%' }} spacing={2} justifyContent="center" alignItems="center">
              <LoadingCardItem />
              <LoadingCardItem />
              <LoadingCardItem />
            </Stack>
          )}

          {(!isLoadingCompanyLinks && !(companyLinksData as any)?.results?.length) && (
            <Stack sx={{ width: '100%' }} spacing={2} justifyContent="center" alignItems="center">
              <EmptyContent
                title={t('deal_flow.detail.linked_companies.empty')}
                description={t('deal_flow.detail.linked_companies.empty_description')}
                sx={{ width: '100%' }}
              />
            </Stack>
          )}

          {(!isLoadingCompanyLinks && !!(companyLinksData as any)?.results?.length) && (
            <Stack direction="column" gap={2} justifyContent="start" alignItems="start" sx={{ width: '100%' }}>
              <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                {(companyLinksData as any)?.results?.map((link: IDealCompanyLink) => (
                  <Fragment key={link.id}>
                    <ListItem
                      sx={{ pt: 1 }}
                      secondaryAction={
                        <Stack direction="row" gap={1} justifyContent="center" alignItems="center">
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={() => handleRemoveCompany(link.id)}
                            disabled={isRemovingCompanyLink}
                          >
                            {t('deal_flow.detail.linked_companies.remove')}
                          </Button>
                        </Stack>
                      }>
                      <ListItemText
                        sx={{ pr: 4 }}
                        primary={
                          <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
                            <Link
                              to={paths.dashboard.companies.detail(link.company_profile.id, tenantType, link.company_profile.company_type as TenantType)}
                              onClick={(e) => {
                                if (!onOpenCompany || e.metaKey || e.ctrlKey || e.shiftKey) return;
                                e.preventDefault();
                                onOpenCompany(link.company_profile.id);
                              }}
                              style={{
                                textDecoration: 'none',
                                color: theme.palette.text.primary,
                                cursor: 'pointer',

                              }}
                            >
                              <TruncatedTextField
                                text={link.company_profile.company_name}
                                mode="characters"
                                limit={30}
                                showMoreEnabled={false}
                                variant="subtitle2"
                                sx={{
                                  textTransform: 'capitalize',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                }}
                              />
                            </Link>
                          </Stack>
                        }
                        secondary={t(`enums.entity_link_type.${link.type}`)}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    <Divider variant="middle" component="li" />
                  </Fragment>
                ))}
              </List>

              <Stack direction="row" justifyContent="center" alignItems="center" sx={{ width: '100%', mt: 2 }}>
                <Pagination
                  count={(companyLinksData as any)?.count ? Math.ceil((companyLinksData as any).count / pageOptions.per_page) : 1}
                  page={pageOptions.page}
                  onChange={onChangePage}
                  size="small"
                />
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={addLinkControl.value}
        title={t('deal_flow.detail.linked_companies.add_dialog_title')}
        onClose={onLinkModalClose}
        content={
          <Stack spacing={3} sx={{ pt: 2 }}>
            <Autocomplete
              multiple={false}
              filterSelectedOptions
              options={linkableCompanies || []}
              loading={isSearchingCompanies}
              getOptionLabel={(option) => option.company_name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('deal_flow.detail.linked_companies.company_autocomplete_label')}
                  placeholder={t('deal_flow.detail.linked_companies.company_autocomplete_placeholder')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSearchingCompanies ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              onInputChange={(_event, value) => onSearchQueryChange(value)}
              onChange={(_event, value) => setSelectedCompany(value as ICompanyProfile)}
              value={selectedCompany}
            />

            <Autocomplete
              disablePortal
              options={Object.values(EntityLinkType)}
              getOptionLabel={(option) => t(`enums.entity_link_type.${option}`)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('deal_flow.detail.linked_companies.link_type_label')}
                />
              )}
              onChange={(_event, value) => setSelectedLinkType(value as EntityLinkType)}
              value={selectedLinkType}
            />
          </Stack>
        }
        action={
          <Button
            variant="contained"
            disabled={!selectedCompany || isAddingCompanyLinks}
            onClick={handleAddCompanies}
          >
            {t('deal_flow.detail.linked_companies.add')}
          </Button>
        }
      />
    </>
  );
}